import { useEffect, useMemo, useState } from "react";
import { Bookmark, Check, Plus, Trash2, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import {
  buildPresets,
  currentQuarterRange,
  deleteFilter,
  listSavedFilters,
  saveFilter,
  type SavedFilter,
  type SavedFilterPayload,
} from "@/lib/savedFilters";

interface SavedFiltersBarProps {
  /** ID de la empresa activa, usado como scope para los filtros guardados. */
  companyId: string;
  /** Estado actual de filtros del Libro, para "Guardar filtro actual". */
  currentPayload: SavedFilterPayload;
  /** ID del filtro actualmente activo (o null si ninguno). */
  activeFilterId: string | null;
  /** Aplica un filtro guardado o preset. */
  onApply: (filter: SavedFilter) => void;
  /** Limpia el filtro activo (vuelve a "Sin filtro"). */
  onClear: () => void;
}

/**
 * Componente UI para presets y filtros guardados (Entrega 4.2).
 * - Lista de presets de fábrica + filtros del usuario.
 * - "Guardar filtro actual" con popover para nombrarlo.
 * - Eliminar filtros guardados (no presets).
 */
const SavedFiltersBar = ({
  companyId,
  currentPayload,
  activeFilterId,
  onApply,
  onClear,
}: SavedFiltersBarProps) => {
  const { toast } = useToast();
  const [savedList, setSavedList] = useState<SavedFilter[]>([]);
  const [open, setOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [newName, setNewName] = useState("");

  const presets = useMemo(
    () => buildPresets({ currentQuarter: currentQuarterRange() }),
    [],
  );

  // Cargar al montar y cuando cambia la empresa.
  useEffect(() => {
    setSavedList(listSavedFilters(companyId));
  }, [companyId]);

  const refresh = () => setSavedList(listSavedFilters(companyId));

  const activeFilter =
    presets.find((p) => p.id === activeFilterId) ||
    savedList.find((f) => f.id === activeFilterId) ||
    null;

  const hasActiveLocal =
    Object.keys(currentPayload.columns).length > 0 ||
    currentPayload.onlyRecurrent ||
    currentPayload.onlyWithIrpf ||
    currentPayload.onlyCurrentQuarter;

  const handleSave = () => {
    const trimmed = newName.trim();
    if (!trimmed) {
      toast({ title: "Nombre requerido", description: "Pon un nombre al filtro.", variant: "destructive" });
      return;
    }
    saveFilter(companyId, trimmed, currentPayload);
    setNewName("");
    setSaveOpen(false);
    refresh();
    toast({ title: "Filtro guardado", description: `«${trimmed}» disponible en "Mis filtros".` });
  };

  const handleDelete = (id: string, name: string) => {
    deleteFilter(companyId, id);
    refresh();
    toast({ title: "Filtro eliminado", description: `«${name}» se ha borrado.` });
  };

  return (
    <div className="flex items-center gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5">
            <Bookmark className="h-3 w-3" />
            {activeFilter ? `Filtro: ${activeFilter.name}` : "Filtros"}
            <ChevronDown className="h-3 w-3 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-2">
          <div className="text-[10px] font-semibold uppercase text-muted-foreground px-2 py-1">
            Predefinidos
          </div>
          {presets.map((p) => {
            const isActive = activeFilterId === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  onApply(p);
                  setOpen(false);
                }}
                className={`w-full text-left text-xs px-2 py-1.5 rounded hover:bg-muted flex items-center gap-2 ${
                  isActive ? "bg-muted font-medium" : ""
                }`}
              >
                {isActive ? <Check className="h-3 w-3" /> : <span className="w-3" />}
                <span className="truncate">{p.name}</span>
              </button>
            );
          })}

          <div className="border-t my-2" />

          <div className="text-[10px] font-semibold uppercase text-muted-foreground px-2 py-1">
            Mis filtros
          </div>
          {savedList.length === 0 ? (
            <div className="text-xs text-muted-foreground italic px-2 py-2">
              Aún no has guardado ningún filtro.
            </div>
          ) : (
            savedList.map((f) => {
              const isActive = activeFilterId === f.id;
              return (
                <div
                  key={f.id}
                  className={`flex items-center gap-1 rounded hover:bg-muted ${
                    isActive ? "bg-muted" : ""
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => {
                      onApply(f);
                      setOpen(false);
                    }}
                    className="flex-1 text-left text-xs px-2 py-1.5 flex items-center gap-2"
                  >
                    {isActive ? <Check className="h-3 w-3" /> : <span className="w-3" />}
                    <span className="truncate">{f.name}</span>
                  </button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-muted-foreground hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(f.id, f.name);
                    }}
                    title="Eliminar"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              );
            })
          )}

          {activeFilterId && (
            <>
              <div className="border-t my-2" />
              <button
                type="button"
                onClick={() => {
                  onClear();
                  setOpen(false);
                }}
                className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-muted text-muted-foreground"
              >
                Quitar filtro activo
              </button>
            </>
          )}
        </PopoverContent>
      </Popover>

      <Popover open={saveOpen} onOpenChange={setSaveOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1.5"
            disabled={!hasActiveLocal}
            title={
              hasActiveLocal
                ? "Guardar filtros activos como nuevo filtro"
                : "Activa algún filtro para poder guardarlo"
            }
          >
            <Plus className="h-3 w-3" />
            Guardar filtro
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-64 p-3">
          <div className="text-xs font-medium mb-2">Nombre del filtro</div>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Ej: Combustible Q2"
            className="h-8 text-xs"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSave();
              }
            }}
            autoFocus
          />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setSaveOpen(false)}>
              Cancelar
            </Button>
            <Button size="sm" className="h-7 text-xs" onClick={handleSave}>
              Guardar
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default SavedFiltersBar;
